// Besoins en bénévoles des matchs, par équipe (D-119) : ce que l'onglet Bénévolat › Matchs et les fiches du bureau
// demandent à la base. Le bureau seul règle (les fonctions le vérifient) ; elles appliquent aux matchs à domicile à venir.
import { supabase } from './supabase';
import type { Busy, ClubNeed, HintKind, ThankReason, Unit, UnitRule } from '../domain/besoins';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function must<T = unknown>(q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}
const rpc = <T>(fn: string, args: Record<string, unknown> = {}) => must<T>(db().rpc(fn, args));

export interface StoredRule extends UnitRule { team_id: string; squad_id: string | null }
export interface NeedsBoard {
  /** Le club n'a rien réglé : les trois besoins par défaut. */
  default: boolean;
  roles: ClubNeed[];
  units: Unit[];
  rules: StoredRule[];
  hints: { team_id: string; squad_id: string | null; kind: HintKind; answer: 'yes' | 'no' }[];
}
/** Un créneau pris qu'une règle aurait retiré : au bureau de trancher. */
export interface KeptSlot { slot_id: string; label: string; title: string; date: string; people: { user_id: string; name: string; g: string }[] }
export interface ApplyReport { matches: number; hand: number; changed: number; kept: KeptSlot[] }

export const needsBoard = (clubId: string) => rpc<NeedsBoard | null>('volunteer_needs_board', { p_club: clubId });
export const saveClubNeeds = (clubId: string, roles: ClubNeed[]) =>
  rpc<ApplyReport>('volunteer_roles_save', { p_club: clubId, p_roles: roles.map(r => ({ label: r.label, needed: r.needed })) });
export const saveUnitRules = (teamId: string, squadId: string | null, rules: UnitRule[]) =>
  rpc<ApplyReport>('volunteer_unit_rules_save', { p_team: teamId, p_squad: squadId, p_rules: rules });
/** La suggestion selon le niveau : « Oui » (la règle appliquée, son compte rendu) ou « Non merci » (null). */
export const answerHint = (teamId: string, squadId: string | null, kind: HintKind, yes: boolean) =>
  rpc<ApplyReport | null>('volunteer_unit_hint', { p_team: teamId, p_squad: squadId, p_kind: kind, p_yes: yes });

export interface MatchPlan { unit: string; hand: boolean; planned: string[] }
export const matchPlan = (eventId: string) => rpc<MatchPlan | null>('volunteer_match_plan', { p_event: eventId });
export const saveMatchNeeds = (eventId: string, needs: { slot_id: string | null; label: string; needed: number }[]) =>
  rpc<{ added: number; removed: number; changed: number; thanked: number }>('volunteer_match_needs_save', { p_event: eventId, p_needs: needs });
/** « Retirer et prévenir » : chaque inscrit reçoit son merci, le créneau part. */
export const dropSlot = (slotId: string) => rpc<number>('volunteer_slot_drop', { p_slot: slotId });

export interface SlotPerson { user_id: string; name: string; created_at: string; by_bureau: boolean; minor: boolean; g: string; vous: boolean; guest: boolean }
export const slotPeople = async (slotId: string) => (await rpc<SlotPerson[]>('volunteer_slot_people', { p_slot: slotId })) ?? [];
export const thankSignup = (slotId: string, userId: string, reason: ThankReason) =>
  rpc<void>('volunteer_signup_thank', { p_slot: slotId, p_user: userId, p_reason: reason });

export interface Candidate { user_id: string; name: string; caps: string; minor: boolean; done: number; busy: Busy[] }
export const slotCandidates = async (slotId: string) => (await rpc<Candidate[]>('volunteer_slot_candidates', { p_slot: slotId })) ?? [];
export const addSignup = (slotId: string, userId: string) => rpc<void>('volunteer_signup_add', { p_slot: slotId, p_user: userId });
